import { useState, useEffect } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Users, Shield, X } from "lucide-react";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
});

export default function AdminUsers() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });

  const fetchAdmins = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, {
        credentials: "include",
        headers: authHeaders(),
      });
      if (res.ok) setAdmins(await res.json());
    } catch {
      toast.error("Error al cargar administradores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const resetForm = () => {
    setForm({ name: "", email: "", phone: "", password: "" });
    setEditUser(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("El nombre es requerido");
    if (!editUser && !form.email.trim()) return toast.error("El email es requerido");
    if (!editUser && !form.password) return toast.error("La contrasena es requerida");

    try {
      const url = editUser
        ? `${API}/admin/users/${editUser.user_id}`
        : `${API}/admin/users`;
      const method = editUser ? "PUT" : "POST";
      const payload = { name: form.name.trim(), phone: form.phone.trim() };
      if (!editUser) {
        payload.email = form.email.trim();
        payload.password = form.password;
      }
      if (editUser && form.password) {
        payload.password = form.password;
      }

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }

      toast.success(editUser ? "Administrador actualizado" : "Administrador creado");
      resetForm();
      fetchAdmins();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al guardar");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API}/admin/users/${deleteTarget}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success("Administrador eliminado");
      setDeleteTarget(null);
      fetchAdmins();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al eliminar");
    }
  };

  const openEdit = (admin) => {
    setEditUser(admin);
    setForm({ name: admin.name, email: admin.email, phone: admin.phone || "", password: "" });
    setShowForm(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2" data-testid="admin-users-title">
            <Shield className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            Administradores
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Gestiona los administradores globales</p>
        </div>
        <Button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="bg-[#1B4D3E] hover:bg-[#163F33] text-white"
          data-testid="admin-add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo Admin
        </Button>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="admin-user-form">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-[#0F172A]">
                {editUser ? "Editar Administrador" : "Nuevo Administrador"}
              </h3>
              <button onClick={resetForm}>
                <X className="w-5 h-5 text-[#64748B]" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Nombre</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nombre completo"
                  data-testid="admin-form-name"
                />
              </div>
              {!editUser && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="email@ejemplo.com"
                    data-testid="admin-form-email"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Telefono (opcional)</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="55 1234 5678"
                  data-testid="admin-form-phone"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {editUser ? "Nueva Contrasena (dejar vacio para no cambiar)" : "Contrasena"}
                </Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editUser ? "Sin cambios" : "Min. 6 caracteres"}
                  data-testid="admin-form-password"
                />
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Button variant="outline" className="flex-1" onClick={resetForm}>Cancelar</Button>
              <Button
                className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white"
                onClick={handleSave}
                data-testid="admin-form-save-btn"
              >
                {editUser ? "Guardar Cambios" : "Crear Admin"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Admin list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : admins.length === 0 ? (
        <Card className="card-default">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-[#64748B] mx-auto mb-3 opacity-50" />
            <p className="text-[#64748B]">No hay administradores registrados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="admin-users-list">
          {admins.map((admin) => (
            <Card key={admin.user_id} className="card-default">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white font-bold text-sm">
                    {admin.name?.[0] || "A"}
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">{admin.name}</p>
                    <p className="text-xs text-[#64748B]">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      admin.status === "active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {admin.status === "active" ? "Activo" : "Inactivo"}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(admin)}
                    data-testid={`admin-edit-${admin.user_id}`}
                  >
                    <Pencil className="w-4 h-4 text-[#64748B]" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTarget(admin.user_id)}
                    data-testid={`admin-delete-${admin.user_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar administrador?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara la cuenta y sus sesiones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-confirm-delete-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
